<template>
    <modal-controller-base contentClass="light-light-background purple-purple-color">
        <template #header>
            <custom-header back-button :title="$t('titleRoute.feedback')">
                <template #start>
                    <ion-button @click="modalController.dismiss()" class="purple-light-color">
                        <ion-icon :icon="chevronBack" />
                        {{ $t("button.back") }}
                    </ion-button>
                </template>
            </custom-header>
        </template>

        <ion-grid class="p-3 max-height max-height-100 overflow-auto d-flex flex-d-c">
            <ion-row>
                <ion-col class="fs-smaller grey1-grey1-color">
                    {{ $t(`placeholder.issueType`) }}
                </ion-col>
            </ion-row>

            <ion-row>
                <ion-col>
                    <select-simple :placeholder="$t('placeholder.issueType')" :options="issueTypeOptions"
                        v-model="issueType" @change="
                            resetValues(
                                issueType === ISSUE_TYPE.BUG
                                    ? feedbackBugEl
                                    : feedbackFeatureEl,
                            )
                            " select-class="grey4-grey4-background" />
                </ion-col>
            </ion-row>

            <ion-row>
                <ion-col class="fs-smaller grey1-grey1-color">
                    {{ $t(`placeholder.title`) }}*
                </ion-col>
            </ion-row>

            <ion-row>
                <ion-col>
                    <input-simple v-model="name" input-class="grey4-grey4-background" />
                </ion-col>
            </ion-row>

            <feedback-bug v-model="descriptionTemplate" :files="files" @file="addFiles" ref="feedbackBugEl"
                :show="issueType === ISSUE_TYPE.BUG" />

            <feedback-feature v-model="descriptionTemplate" ref="feedbackFeatureEl"
                :show="issueType === ISSUE_TYPE.FEATURE" />


            <ion-row>
                <ion-col class="fs-smaller grey1-grey1-color">
                    {{ $t(`placeholder.contactWay`) }}
                </ion-col>
            </ion-row>

            <ion-row>
                <ion-col>
                    <select-simple :placeholder="$t('placeholder.contactWay')" :options="contactWayOptions"
                        v-model="contactWay.way" select-class="grey4-grey4-background" />
                </ion-col>
            </ion-row>

            <div
                v-if="contactWay.way === TICKET_CONTACT_WAY.EMAIL || contactWay.way === TICKET_CONTACT_WAY.EMAIL_OR_PHONE">
                <ion-row>
                    <ion-col class="fs-smaller grey1-grey1-color">
                        {{ $t(`placeholder.email`) }}
                    </ion-col>
                </ion-row>

                <ion-row>
                    <ion-col>
                        <input-simple v-model="contactWay.email"
                            :validate-option="{ name: 'email', label: $t('label.email') }"
                            input-class="grey4-grey4-background" />
                    </ion-col>
                </ion-row>
            </div>
            <div
                v-if="contactWay.way === TICKET_CONTACT_WAY.PHONE || contactWay.way === TICKET_CONTACT_WAY.EMAIL_OR_PHONE">
                <ion-row>
                    <ion-col class="fs-smaller grey1-grey1-color">
                        {{ $t(`placeholder.phone`) }}
                    </ion-col>
                </ion-row>

                <ion-row>
                    <ion-col>
                        <input-simple v-model="contactWay.phone"
                            :validate-option="{ name: 'phone', label: $t('label.phone') }"
                            input-class="grey4-grey4-background" />
                    </ion-col>
                </ion-row>
            </div>

            <ion-row class="flex-grow-1">
                <ion-col class="center-x ion-align-items-end">
                    <button-fix @click="throttleTicket" :disabled="!(issueType === ISSUE_TYPE.BUG
                        ? feedbackBugEl?.meta?.valid
                        : feedbackFeatureEl?.meta?.valid) || !name
                        ">
                        {{ $t("button.send") }}
                    </button-fix>
                </ion-col>
            </ion-row>
        </ion-grid>
    </modal-controller-base>
</template>

<script setup lang="ts">import { ref as _ref } from 'vue';

import {
    IonButton,
    IonCol,
    IonGrid,
    IonIcon,
    IonRow,
    modalController,
} from "@ionic/vue";
import { ref, onMounted } from "vue";
import { Device } from "@capacitor/device";
import { App } from "@capacitor/app";
import { Capacitor } from "@capacitor/core";
import { t } from "@/plugins/vue-i18n";
import { request } from "@/api-connectors/basicRequests";
import { useThrottleFn } from "@vueuse/core";

import ModalControllerBase from "@/components/modal/modalControllerModals/ModalControllerBase.vue";
import CustomHeader from "@/components/basePageElements/CustomHeader.vue";
import InputSimple from "@/components/input/InputSimple.vue";
import ButtonFix from "@/components/button/ButtonFix.vue";
import SelectSimple from "@/components/selects/SelectSimple.vue";
import FeedbackBug from "@/components/form/FeedbackBug.vue";
import FeedbackFeature from "@/components/form/FeedbackFeature.vue";

import { replaceToast } from "@/functions/baseToast";

import { useLoader } from "@/composables/useLoader";
import { useLanguageArray } from "@/composables/useLanguageArray";

import { FeedbackTicketTemplate } from "@/types/request";
import { ISSUE_TYPE, TICKET_PRIORITY, TICKET_CONTACT_WAY } from "@/types/enum/general";
import { ION_VARIANT } from "@/types/enum/ionicEnum";

import { chevronBack } from "ionicons/icons";
import { useUserStore } from "@/stores/user";

let name = _ref<string>("");
const issueType = _ref<ISSUE_TYPE>(ISSUE_TYPE.BUG);
const priority = _ref<TICKET_PRIORITY>(TICKET_PRIORITY.MEDIUM);

const profile = useUserStore().sessionInfo
const contactWay = _ref<{ way: TICKET_CONTACT_WAY, email: string, phone: string }>({ way: TICKET_CONTACT_WAY.EMAIL, email: profile.email ?? "", phone: "" })
let descriptionTemplate = _ref<FeedbackTicketTemplate>(
    new FeedbackTicketTemplate(),
);
const files = _ref<File[]>([]);
const feedbackBugEl = ref();
const feedbackFeatureEl = ref();

const { showLoading, hideLoading } = useLoader();

const resetValues = (ref: any): void => {
    name.value = "";
    descriptionTemplate.value = new FeedbackTicketTemplate();
    files.value.length = 0;
    loadVersions();
    ref.resetForm();
};

const { content: issueTypeOptions } = useLanguageArray(
    [
        { text: `enumT.issueType.${ISSUE_TYPE.BUG}`, value: ISSUE_TYPE.BUG },
        {
            text: `enumT.issueType.${ISSUE_TYPE.FEATURE}`,
            value: ISSUE_TYPE.FEATURE,
        },
    ],
    "text",
);

const { content: contactWayOptions } = useLanguageArray(
    [
        { text: `enumT.contactWay.${TICKET_CONTACT_WAY.NONE}`, value: TICKET_CONTACT_WAY.NONE },
        { text: `enumT.contactWay.${TICKET_CONTACT_WAY.EMAIL}`, value: TICKET_CONTACT_WAY.EMAIL },
        { text: `enumT.contactWay.${TICKET_CONTACT_WAY.PHONE}`, value: TICKET_CONTACT_WAY.PHONE },
        { text: `enumT.contactWay.${TICKET_CONTACT_WAY.EMAIL_OR_PHONE}`, value: TICKET_CONTACT_WAY.EMAIL_OR_PHONE },
    ],
    "text",
);

const sendTicket = async () => {
    await showLoading();

    const formData = new FormData();

    for (const [key, value] of Object.entries(descriptionTemplate.value)) {
        if (value !== undefined) {
            formData.append(key, value);
        }
    }

    if (files.value.length > 0) {
        for (const file of files.value) {
            formData.append("files", file);
        }
    }

    formData.append("contactWay", contactWay.value.way)
    if (contactWay.value.way === TICKET_CONTACT_WAY.EMAIL || contactWay.value.way === TICKET_CONTACT_WAY.EMAIL_OR_PHONE) {
        formData.append("contactEmail", contactWay.value.email)
    }

    if (contactWay.value.way === TICKET_CONTACT_WAY.PHONE || contactWay.value.way === TICKET_CONTACT_WAY.EMAIL_OR_PHONE) {
        formData.append("contactPhone", contactWay.value.phone)
    }

    formData.append("name", name.value);
    formData.append("priority", priority.value);
    formData.append("type", issueType.value);

    const response = await request({
        method: "POST",
        route: "feedback/mobile/ticket",
        body: formData,
    });

    if (response === undefined) {
        await replaceToast({
            message: t("error.sendTicket"),
            color: ION_VARIANT.DANGER,
            id: "error-issue-report",
        });
        await hideLoading();
        return;
    }

    await modalController.dismiss();

    replaceToast({
        message: t("success.sendTicket"),
        id: "success-issue-report",
    });
    await hideLoading();
};

const throttleTicket = useThrottleFn(sendTicket, 1000);

const addFiles = (inputFiles: FileList | File | undefined): void => {
    if (!inputFiles) return;
    Object.values(inputFiles).forEach((file) => {
        files.value.push(file);
    });
};

const loadVersions = async () => {
    descriptionTemplate.value.deviceId = (await Device.getId()).identifier;
    descriptionTemplate.value.webVersion =
        process.env.VUE_APP_VERSION_NAME ?? "UNKNOWN";

    const {
        model,
        operatingSystem,
        osVersion,
        webViewVersion,
        platform,
        manufacturer,
    } = await Device.getInfo();
    descriptionTemplate.value.deviceModel = `${manufacturer}, ${model} (${platform})`;
    descriptionTemplate.value.osVersion = `${operatingSystem} ${osVersion}`;
    descriptionTemplate.value.webViewVersion = webViewVersion;

    if (Capacitor.isNativePlatform()) {
        const { build, version } = await App.getInfo();
        descriptionTemplate.value.binaryVersion = `${version} (${build})`;
    }
};

onMounted(loadVersions);
</script>
